import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { Translate, withLocalize } from 'react-localize-redux';
import { ServiceType, RequestStatus } from '../../Request/types';
import { getRequestStatusName, surgeryStatusValues } from '../../Request/utils';
import { getServicesInvestigationService } from '../../../../services/agenda';
import { useDepartments } from '../../../../hooks';
import {
  EMPTY_SURGERY_ANALYTICS_FILTERS,
  SurgeryAnalyticsFiltersQuery,
} from '../types';


export interface SurgeryAnalyticsFiltersProps {
  uuidInvestigation: string;
  value: SurgeryAnalyticsFiltersQuery;
  onChange: (next: SurgeryAnalyticsFiltersQuery) => void;
  onApply: () => void;
}

const SurgeryAnalyticsFiltersComponent: React.FC<SurgeryAnalyticsFiltersProps & { translate: (id: string) => string }> = ({
  uuidInvestigation,
  value,
  onChange,
  onApply,
  translate,
}) => {
  const { researchers } = useDepartments();
  const [services, setServices] = useState<
    { id: number; label: string; serviceId: number; serviceLabel: string }[]
  >([]);

  useEffect(() => {
    let cancelled = false;
    getServicesInvestigationService(uuidInvestigation, ServiceType.SURGERY)
      .then((response) => {
        if (cancelled || response.status !== 200) {
          return;
        }
        setServices(
          (response.servicesInvestigation ?? []).map((si) => ({
            id: si.id,
            label: si.description || si.name,
            serviceId: si.service?.id,
            serviceLabel: si.service?.name ?? si.name,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setServices([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [uuidInvestigation]);

  const procedureOptions = useMemo(() => {
    const byService = new Map<number, string>();
    services.forEach((service) => {
      if (service.serviceId) {
        byService.set(service.serviceId, service.serviceLabel);
      }
    });
    return Array.from(byService.entries()).map(([id, label]) => ({ id, label }));
  }, [services]);

  const statusOptions = useMemo(
    () =>
      // SHER-935: one surgical status list, shared with the waiting-list filter menu, so the
      // two menus cannot drift; and the label built the way every other status label is.
      surgeryStatusValues.map((status) => ({
        value: status,
        label: translate(
          `pages.hospital.services.request_status.${getRequestStatusName(status)}`,
        ),
      })),
    [translate],
  );

  const selectedServices = services.filter((service) =>
    value.serviceInvestigationIds.includes(service.id),
  );
  const selectedProcedures = procedureOptions.filter((option) =>
    value.procedureServiceIds.includes(option.id),
  );
  const selectedSurgeons = researchers.filter((researcher) =>
    value.surgeonResearcherUuids.includes(researcher.uuid),
  );
  const selectedStatuses = statusOptions.filter((option) =>
    value.statuses.includes(option.value),
  );

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        <Translate id="hospital.analytics.surgery.filters.title" />
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={3}>
          <Autocomplete
            multiple
            options={services}
            getOptionLabel={(option) => option.label}
            value={selectedServices}
            onChange={(_, next) =>
              onChange({
                ...value,
                serviceInvestigationIds: next.map((item) => item.id),
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('hospital.analytics.surgery.filters.service')}
                size="small"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Autocomplete
            multiple
            options={procedureOptions}
            getOptionLabel={(option) => option.label}
            value={selectedProcedures}
            onChange={(_, next) =>
              onChange({
                ...value,
                procedureServiceIds: next.map((item) => item.id),
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('hospital.analytics.surgery.filters.procedure')}
                size="small"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Autocomplete
            multiple
            options={researchers}
            getOptionLabel={(option) => `${option.name} ${option.surnames}`}
            value={selectedSurgeons}
            onChange={(_, next) =>
              onChange({
                ...value,
                surgeonResearcherUuids: next.map((item) => item.uuid),
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('hospital.analytics.surgery.filters.surgeon')}
                size="small"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Autocomplete
            multiple
            options={statusOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, selected) => option.value === selected.value}
            value={selectedStatuses}
            onChange={(_, next) =>
              onChange({
                ...value,
                statuses: next.map((item) => item.value),
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate('hospital.analytics.surgery.filters.status')}
                size="small"
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            {selectedServices.map((service) => (
              <Chip key={`si-${service.id}`} size="small" label={service.label} />
            ))}
            {selectedProcedures.map((procedure) => (
              <Chip key={`proc-${procedure.id}`} size="small" label={procedure.label} />
            ))}
            {selectedSurgeons.map((surgeon) => (
              <Chip
                key={surgeon.uuid}
                size="small"
                label={`${surgeon.name} ${surgeon.surnames}`}
              />
            ))}
            {selectedStatuses.map((status) => (
              <Chip key={status.value} size="small" label={status.label} />
            ))}
            <Button variant="contained" size="small" onClick={onApply}>
              <Translate id="hospital.analytics.surgery.filters.apply" />
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => onChange(EMPTY_SURGERY_ANALYTICS_FILTERS)}
            >
              <Translate id="hospital.analytics.surgery.filters.clear" />
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default withLocalize(SurgeryAnalyticsFiltersComponent);
